// Issue #363 — shared contract for the admin-editable company profile and the
// Markdown legal pages (service framework, cancellation/refund, terms of use).
// Storage: app_settings keys COMPANY_PROFILE_SETTING_KEY and
// legalPageSettingKey(slug). Public reads never require auth; writes are admin-only.

export const COMPANY_PROFILE_SETTING_KEY = 'company_profile';

export interface CompanyProfile {
  legalName: string;
  address: string;
  email: string;
  phone: string;
  whatsapp: string;
  taxOffice: string;
  taxNumber: string;
}

export type CompanyProfileField = keyof CompanyProfile;

export const COMPANY_PROFILE_FIELDS: readonly CompanyProfileField[] = [
  'legalName',
  'address',
  'email',
  'phone',
  'whatsapp',
  'taxOffice',
  'taxNumber',
] as const;

/** Seed values used when no stored profile exists (owner-confirmed 2026-10-21). */
export const COMPANY_PROFILE_DEFAULTS: CompanyProfile = {
  legalName: 'Muhammed Bilal Benli / MBBE SOFTWARE',
  address: 'Fenerbahçe Mah. İğrip Sk. No: 13 İç Kapı No: 1 Kadıköy / İstanbul',
  email: 'info@mbbe.dev',
  phone: '+90 850 308 30 86',
  whatsapp: '+90 850 308 30 86',
  taxOffice: '',
  taxNumber: '',
};

export const COMPANY_PROFILE_MAX_LENGTH = 300;

export const LEGAL_PAGE_SLUGS = ['hizmet-sozlesmesi', 'iptal-iade-politikasi', 'kullanim-kosullari'] as const;
export type LegalPageSlug = (typeof LEGAL_PAGE_SLUGS)[number];

export type LegalPageLocale = 'tr' | 'en';
export const LEGAL_PAGE_LOCALES: readonly LegalPageLocale[] = ['tr', 'en'] as const;

export const LEGAL_PAGE_MAX_MARKDOWN_LENGTH = 100_000;

export function legalPageSettingKey(slug: LegalPageSlug): string {
  return `legal_page:${slug}`;
}

export interface LegalPageLocaleContent {
  title: string;
  markdown: string;
}

/** Stored value of app_settings[legalPageSettingKey(slug)]. Empty strings mean "use default". */
export interface LegalPageContent {
  tr: LegalPageLocaleContent;
  en: LegalPageLocaleContent;
}

/** GET /api/site/profile (public) */
export interface PublicCompanyProfileResponse {
  legalTitles?: Record<LegalPageSlug, Record<LegalPageLocale, string>>;
  profile: Partial<CompanyProfile>; // empty fields omitted
  updatedAt: string | null;
}

/** GET /api/site/legal-pages/:slug (public). Server applies defaults + EN→TR fallback; placeholders unresolved. */
export interface PublicLegalPageResponse {
  slug: LegalPageSlug;
  tr: LegalPageLocaleContent;
  en: LegalPageLocaleContent;
  updatedAt: string | null;
  isDefault: boolean;
}

/**
 * Admin endpoints (admin auth, fail-closed, audited):
 *   GET  /api/admin/site/profile            -> { profile: CompanyProfile; updatedAt: string | null }
 *   PUT  /api/admin/site/profile            body CompanyProfile -> same as GET
 *   GET  /api/admin/site/legal-pages/:slug  -> { slug; content: LegalPageContent; defaults: LegalPageContent; updatedAt: string | null }
 *   PUT  /api/admin/site/legal-pages/:slug  body LegalPageContent -> same as GET
 */
export interface AdminCompanyProfileResponse {
  profile: CompanyProfile;
  updatedAt: string | null;
}

export interface AdminLegalPageResponse {
  slug: LegalPageSlug;
  content: LegalPageContent;
  defaults: LegalPageContent;
  updatedAt: string | null;
}

/** Placeholders usable in legal Markdown, resolved from CompanyProfile at render time. */
export const LEGAL_PLACEHOLDER_PATTERN = /\{\{(legalName|address|email|phone|whatsapp|taxOffice|taxNumber)\}\}/g;

/** Remove empty-field items with their continuations and table rows atomically.
 * A missing table header removes the entire table, including its separator.
 */
export function resolveLegalPlaceholders(markdown: string, profile: Partial<CompanyProfile>): string {
  const lines = markdown.split('\n');
  const missing = (line: string) => [...line.matchAll(LEGAL_PLACEHOLDER_PATTERN)]
    .some(match => !(profile[match[1] as CompanyProfileField] ?? '').trim());
  const removed = new Set<number>();
  const separator = (line: string) => line.includes('|') && line.trim().replace(/^\||\|$/g, '')
    .split('|').every(cell => /^:?-{3,}:?$/.test(cell.trim()));
  for (let i = 0; i < lines.length; i++) {
    if (lines[i].includes('|') && separator(lines[i + 1] ?? '')) {
      let end = i + 2;
      while (end < lines.length && lines[end].includes('|') && lines[end].trim()) end++;
      for (let row = i; row < end; row++) {
        if (missing(lines[i]) || (row > i + 1 && missing(lines[row]))) removed.add(row);
      }
      i = end - 1;
      continue;
    }
    if (!missing(lines[i])) continue;
    removed.add(i);
    const item = lines[i].match(/^(\s*)(?:[-+*]|\d+[.)])\s+/);
    if (item) {
      for (let next = i + 1; next < lines.length; next++) {
        if (!lines[next].trim()) continue;
        if (lines[next].match(/^\s*/)![0].length <= item[1].length) break;
        removed.add(next);
      }
    }
  }
  return lines.filter((_, index) => !removed.has(index))
    .map(line => line.replace(LEGAL_PLACEHOLDER_PATTERN, (_, field: CompanyProfileField) => (profile[field] ?? '').trim()))
    .join('\n');
}

export { LEGAL_PAGE_DEFAULTS } from './legal-page-defaults';
